import { en as enLocalizations } from "common/config/localizations";
import type { Venue } from "common/types";
import type { PlacesCityParams, PlacesCityResponse, PlacesPageType, PlacesSort, PlacesVenue } from "common/types/places";
import { sanitiseMetaDescription } from "@/lib/meta";
import { slugToTitle } from "@/lib/slug";

type CityData = PlacesCityResponse["data"];

const FILTER_KEYS = ["types", "accessibilities", "accessibility_sections", "q", "sort"] as const;

/** The orders other than the default A to Z. */
export const PLACES_SORTS: PlacesSort[] = ["reviews", "distance", "distance_desc"];

const TYPES_PATTERN = /^[A-Za-z0-9_ &|-]+$/;
const MAX_PARAM_LENGTH = 2000;

/** The API's limit on a search (`q`), shared by the URL parser and the search box. */
export const MAX_SEARCH_LENGTH = 100;

/** History state for a same-page search, sort or filter: the root layout leaves focus on the control that made it. */
export const KEEP_FOCUS_STATE = { keepFocus: true } as never;

/** Fills "%{name}" placeholders; head() runs outside React, so it cannot use useI18N. */
export function fillTemplate(template: string, values: Record<string, string | number>): string {
  return template.replace(/%\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
}

/**
 * Reads a places page's page, filters, search and sort from the URL, dropping anything the API would
 * reject, so a mistyped filter shows the unfiltered place instead of a 404.
 *
 * @param raw TanStack search params.
 * @returns Every known key, undefined where the URL value was empty or rejected.
 */
export function parsePlacesSearch(raw: Record<string, unknown>): PlacesCityParams {
  const text = (value: unknown, maxLength = MAX_PARAM_LENGTH) => {
    // TanStack parses "?accessibilities=743" as the number 743.
    const str = typeof value === "number" && Number.isFinite(value) ? String(value) : value;
    return typeof str === "string" && str.trim() !== "" && str.trim().length <= maxLength
      ? str.trim()
      : undefined;
  };
  const matching = (value: string | undefined, pattern: RegExp) =>
    value !== undefined && pattern.test(value) ? value : undefined;

  const page = Number(raw.page);
  const sort = PLACES_SORTS.find((value) => value === raw.sort);

  // Keys stay present when undefined: TanStack lays this over the raw URL search, so an
  // omitted key would let the rejected raw value through to the loader.
  return {
    page: Number.isInteger(page) && page > 1 ? page : undefined,
    types: matching(text(raw.types), TYPES_PATTERN),
    accessibilities: text(raw.accessibilities),
    accessibility_sections: text(raw.accessibility_sections),
    // The pages no longer offer an opening-hours filter, so an old link opens the place unfiltered.
    openingHoursMode: undefined,
    openingHoursDay: undefined,
    openingHoursTime: undefined,
    q: text(raw.q, MAX_SEARCH_LENGTH),
    sort,
  };
}

/**
 * The place a page is about: the city, or "<area>, <city>" on an area page.
 *
 * @param data Page data.
 * @param areaInCity Formats an area with its city (placesAreaInCity).
 * @returns The place name used in headings and titles.
 */
export function placesPlaceName(
  data: Pick<CityData, "city" | "area">,
  areaInCity: (area: string, city: string) => string,
): string {
  return data.area ? areaInCity(data.area.name, data.city.name) : data.city.name;
}

/**
 * The page type a `types` filter names when it names exactly one, matched case-insensitively.
 *
 * @param types The `types` filter, pipe-separated.
 * @param pageTypes Types that have their own page.
 * @returns That type, or undefined for none, several, or a type without a page.
 */
export function singlePageType(
  types: string | undefined,
  pageTypes: PlacesPageType[] | undefined,
): PlacesPageType | undefined {
  const names = (types ?? "").split("|").map((name) => name.trim()).filter(Boolean);
  if (names.length !== 1) return undefined;
  return pageTypes?.find((type) => type.name.toLowerCase() === names[0].toLowerCase());
}

/**
 * City-page search params for Link / navigate. The app's router is registered untyped, so
 * TanStack types `search` as never; this keeps that one cast in a single place.
 *
 * @param search Page and filters.
 * @returns The same object, typed for the router.
 */
export function placesSearch(search: PlacesCityParams): never {
  return search as never;
}

/** Whether a filter, a search or a non-default order changes the page (the page number does not count). */
export function hasPlacesFilters(search: PlacesCityParams): boolean {
  return FILTER_KEYS.some((key) => search[key] !== undefined && search[key] !== "");
}

/** Whether two places-page searches apply the same filters, search and order, ignoring the page number. */
export function samePlacesFilters(a: PlacesCityParams, b: PlacesCityParams): boolean {
  return FILTER_KEYS.every((key) => (a[key] ?? "") === (b[key] ?? ""));
}

/**
 * Head tags for /places.
 *
 * @param origin Canonical origin, no trailing slash.
 * @param isProduction Robots are only emitted in production; the root layout blocks the rest.
 * @returns Head meta and links.
 */
export function buildPlacesIndexHead(origin: string, isProduction: boolean) {
  return {
    meta: [
      { title: enLocalizations.placesIndexTitle },
      { name: "description", content: sanitiseMetaDescription(enLocalizations.placesIndexDescription) },
      ...(isProduction ? [{ name: "robots", content: "index, follow" }] : []),
    ],
    links: [{ rel: "canonical", href: `${origin}/places` }],
  };
}

/**
 * Head tags for /places/<city>: filtered views and small cities stay out of the index, and each
 * numbered page canonicalises to itself.
 *
 * @param data The city page data, or undefined when it failed to load.
 * @param slug City slug from the URL.
 * @param search Page and filters from the URL.
 * @param origin Canonical origin, no trailing slash.
 * @param isProduction Robots are only emitted in production; the root layout blocks the rest.
 * @returns Head meta and links.
 */
export function buildPlacesCityHead(
  data: CityData | undefined,
  slug: string,
  search: PlacesCityParams,
  origin: string,
  isProduction: boolean,
) {
  return buildPlacesPageHead(data, { citySlug: slug }, search, origin, isProduction);
}

/**
 * Head tags for any /places/<city>[/<area>][/<type>] page; filtered or below-minimum pages are noindex.
 *
 * @param data The page data, or undefined when it failed to load.
 * @param slugs The city slug, plus the area and/or type slug when the page has them.
 * @param search Page and filters from the URL.
 * @param origin Canonical origin, no trailing slash.
 * @param isProduction Robots are only emitted in production; the root layout blocks the rest.
 * @returns Head meta and links.
 */
export function buildPlacesPageHead(
  data: CityData | undefined,
  slugs: { citySlug: string; areaSlug?: string; typeSlug?: string },
  search: PlacesCityParams,
  origin: string,
  isProduction: boolean,
) {
  const { citySlug, areaSlug, typeSlug } = slugs;
  const areaInCity = (area: string, city: string) => fillTemplate(enLocalizations.placesAreaInCity, { area, city });
  const placeName = data
    ? placesPlaceName(data, areaInCity)
    : areaSlug
      ? areaInCity(slugToTitle(areaSlug), slugToTitle(citySlug))
      : slugToTitle(citySlug);
  const typeLabel = data?.type?.label ?? typeSlug?.replace(/-/g, " ");
  const page = search.page ?? 1;
  const filtered = hasPlacesFilters(search);

  const scope = data?.type ?? data?.area;
  const count = (scope ? scope.count : data?.city.count) ?? data?.total_count ?? 0;
  const indexable = scope ? scope.indexable : (data?.city.indexable ?? false);

  const values = { city: placeName, type: typeLabel ?? "", count, page };
  const title = typeLabel
    ? fillTemplate(page > 1 ? enLocalizations.placesTypePagedTitle : enLocalizations.placesTypeTitle, values)
    : fillTemplate(page > 1 ? enLocalizations.placesCityPagedTitle : enLocalizations.placesCityTitle, values);
  const description = sanitiseMetaDescription(
    typeLabel
      ? fillTemplate(page > 1 ? enLocalizations.placesTypePagedDescription : enLocalizations.placesTypeDescription, values)
      : fillTemplate(page > 1 ? enLocalizations.placesCityPagedDescription : enLocalizations.placesCityDescription, values),
  );

  const path = ["places", citySlug, areaSlug, typeSlug].filter(Boolean).join("/");
  const pageUrl = `${origin}/${path}`;
  const canonical = filtered || page === 1 ? pageUrl : `${pageUrl}?page=${page}`;
  const robots = !data || filtered || !indexable ? "noindex, follow" : "index, follow";

  return {
    meta: [
      { title },
      { name: "description", content: description },
      ...(isProduction ? [{ name: "robots", content: robots }] : []),
    ],
    links: [{ rel: "canonical", href: canonical }],
  };
}

/**
 * A city-page venue in the shape VenueCard reads.
 *
 * @param venue One venue from the places API.
 * @returns A Venue with the card's fields filled and the rest left empty.
 */
export function placesVenueToVenue(venue: PlacesVenue): Venue {
  return {
    id: String(venue.google_business_id),
    name: venue.name,
    address: venue.address,
    latitude: venue.latitude,
    longitude: venue.longitude,
    businessType: venue.business_type_label || venue.business_type,
    types: "",
    photo: venue.photo_thumb,
    photoThumb: venue.photo_thumb,
    altText: venue.alt_text,
    reviewDetailsSubmitted: venue.reviews_details_submitted,
    reviewsDetailsSubmitted: venue.reviews_details_submitted,
    venueDataSource: venue.venue_data_source ?? undefined,
    hasExperiences: venue.has_experiences ?? false,
    hasAlerts: venue.has_alerts ?? false,
    hasGeneralUserPhotos: venue.has_general_user_photos ?? false,
    hasGeneralUserTags: venue.has_general_user_tags ?? false,
    latestReviewDateUpdated: venue.latest_review_date_updated ?? "",
    openingHours: [],
    tagIds: "",
    taggingCategories: "",
    distance: "",
    userDistance: "",
    businessStatus: "OPERATIONAL",
    business: {
      id: venue.id,
      slug: venue.slug,
      publicUrl: venue.public_url || venue.slug,
      templateType: venue.template_type,
      isComplex: venue.template_type && venue.template_type !== "Basic" ? 1 : 0,
      venue_summary: venue.venue_summary ?? "",
    },
  } as unknown as Venue;
}
